// MyNumbers — "My Numbers" personal dashboard.
//
// Self-scoped view available to EVERY employee (the only analytics surface most
// staff see). Sources /analytics/personal (own claims/tasks/time — no special
// capability) for KPIs + charts, and /reports/adjuster-wip for the unbilled-WIP
// detail list. Strictly the caller's own data; never team/firm-wide.
// (ATTICUS-ANALYTICS-COMMAND-CENTER-1 enrichment, 2026-06-23.)
import { useState, useEffect, useMemo } from "react";
import { apiFetch, fmt } from "../api";
import { ErrorBanner } from "../components/ui";  // UI-SWEEP-ANALYTICS
import { toneForStatus } from "../components/ui";
import { KpiCard, ChartCard } from "../analytics/AnalyticsKit";
import { Donut, HBar, Trend, TONE_HEX } from "../analytics/charts";
import { ANALYTICS_CSS } from "../analytics/analyticsStyles";

export default function MyNumbers({ user }) {
  const [p, setP] = useState(null);
  const [wip, setWip] = useState({ rows: [], counts: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    Promise.all([
      apiFetch("/analytics/personal"),
      apiFetch("/reports/adjuster-wip?mode=worked").catch(() => ({ rows: [], counts: null })),
    ])
      .then(([pers, w]) => { if (live) { setP(pers); setWip({ rows: w.rows || [], counts: w.counts || null }); setLoading(false); } })
      .catch((e) => { if (live) { setError(String(e.message || e)); setLoading(false); } });
    return () => { live = false; };
  }, []);

  const m = (v) => fmt.money(v);
  const k = p?.kpis || {};
  const statusData = useMemo(() => (p?.by_status || [])
    .map((r) => ({ name: r.status, value: r.count, color: TONE_HEX[toneForStatus(r.status)] })), [p]);
  const insuredData = useMemo(() => (p?.by_insured || []).map((r) => ({ name: r.name, value: r.count })), [p]);
  const uwData = useMemo(() => (p?.by_lead_underwriter || []).map((r) => ({ name: r.name, value: r.count })), [p]);
  const trendData = p?.time_trend || [];

  // WIP grouped by insured (detail list).
  const grouped = useMemo(() => (wip.rows || []).reduce((acc, r) => {
    const ins = r.insured_name || "Unknown Insured";
    (acc[ins] = acc[ins] || []).push(r);
    return acc;
  }, {}), [wip.rows]);
  const groupOrder = useMemo(() => Object.entries(grouped).sort(
    (a, b) => sumHrs(b[1]) - sumHrs(a[1])), [grouped]);

  if (error) return <div className="an-page"><ErrorBanner>Error loading My Numbers: {error}</ErrorBanner></div>;
  const L = loading && !p;

  return (
    <div className="an-page">
      <style>{ANALYTICS_CSS}</style>
      <div className="an-header" style={{ marginBottom: 12 }}>
        <div>
          <div className="an-h-title">My Numbers</div>
          <div className="an-h-sub">Your claims, work, and unbilled time — {user.full_name}</div>
        </div>
      </div>

      <div className="an-stack">
        <div className="an-kpis">
          <KpiCard label="My Open Claims" tone="blue" value={L ? "…" : num(k.open_claims)} sub="lead or staff" />
          <KpiCard label="My Book Exposure" tone="navy" value={L ? "…" : m(k.total_reserve)} sub="total reserve" />
          <KpiCard label="Remaining Reserve" tone="green" value={L ? "…" : m(k.remaining)} sub="on my open claims" />
          <KpiCard label="My Open Tasks" tone={k.overdue_tasks > 0 ? "red" : undefined}
                   value={L ? "…" : num(k.open_tasks)} sub={k.overdue_tasks ? `${k.overdue_tasks} overdue` : "none overdue"} />
          <KpiCard label="Unbilled WIP" tone="amber" value={L ? "…" : `${Number(k.wip_hours || 0).toFixed(1)} hrs`}
                   sub={L ? "" : `${m(k.wip_dollars)} · ${num(k.wip_claims)} claims`} />
        </div>

        <div className="an-grid">
          <ChartCard title="My Claims by Status" loading={L} empty={!statusData.length}>
            <Donut data={statusData} />
          </ChartCard>

          <ChartCard title="My Time Logged" subtitle="billable + non-billable hours, last 6 months" loading={L}
                     empty={!trendData.some((d) => d.hours > 0)}>
            <Trend data={trendData} xKey="month" series={[{ key: "hours", label: "Hours", color: "#3b6fd4" }]} />
          </ChartCard>

          <ChartCard title="My Claims by Insured" subtitle="top 12 insured companies" loading={L} empty={!insuredData.length}
                     height={Math.max(240, insuredData.length * 24)}>
            <HBar data={insuredData} height={Math.max(220, insuredData.length * 24)} color="#3b6fd4" />
          </ChartCard>

          <ChartCard title="My Claims by Lead Underwriter" subtitle="top 12 lead underwriters" loading={L}
                     empty={!uwData.length}
                     unavailable={!L && p && !uwData.length ? { status: "No data", reason: "None of your claims have a Lead Underwriter party assigned." } : undefined}
                     height={Math.max(240, uwData.length * 24)}>
            <HBar data={uwData} height={Math.max(220, uwData.length * 24)} color="#14b8a6" />
          </ChartCard>

          <ChartCard title="Unbilled WIP by Insured" subtitle="your worked, unbilled billable hours" wide
                     loading={L} empty={!groupOrder.length}
                     footer={wip.counts ? <span style={{ fontWeight: 700 }}>Total WIP: {Number(wip.counts.total_hours || 0).toFixed(2)} hrs</span> : null}>
            <div className="mn-wip">
              {groupOrder.map(([ins, claims]) => (
                <div key={ins} className="mn-wip-group">
                  <div className="mn-wip-head">
                    <span>{ins}</span>
                    <span className="mono">{sumHrs(claims).toFixed(2)} hrs</span>
                  </div>
                  {claims.sort((a, b) => Number(b.unbilled_hours || 0) - Number(a.unbilled_hours || 0)).map((r) => (
                    <div key={r.claim_number} className="mn-wip-row">
                      <span className="mono" style={{ color: "var(--blue)", fontWeight: 600, flex: 1 }}>{r.claim_number}</span>
                      <span style={{ color: "var(--text2)" }}>{Number(r.unbilled_hours || 0).toFixed(2)} hrs</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

function sumHrs(rows) { return rows.reduce((s, r) => s + Number(r.unbilled_hours || 0), 0); }
function num(n) { return n == null ? "—" : Number(n).toLocaleString(); }
